import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Tooltip
} from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import { addDays, startOfDay, endOfDay } from 'date-fns';
import { reduxFormResolver } from '#helper/rhf-resolver';
import RHFDateTimeField from '#component/form/rhf/rhf-date-time-field';
import RHFTextField from '#component/form/rhf/rhf-text-field';
import { FormContainer, KeyDisplayContainer, KeyText, StyledAlert } from './styles';
import { validate, MAX_EXPIRATION_DATE, MAX_LABEL_LENGTH } from './validation';

const CreateApiKeyDialog = ({
  open,
  onClose,
  onSubmit
}) => {
  const [step, setStep] = useState('form'); // 'form' or 'created'
  const [createdApiKey, setCreatedApiKey] = useState(null);

  const methods = useForm({
    resolver: reduxFormResolver(validate),
    defaultValues: { label: '', expirationDate: '' }
  });

  const { handleSubmit, formState: { isSubmitting, isValid }, reset } = methods;

  useEffect(() => {
    if (open) {
      reset({ label: '', expirationDate: '' });
      setStep('form');
      setCreatedApiKey(null);
    }
  }, [open, reset]);

  const handleClose = () => {
    setStep('form');
    setCreatedApiKey(null);
    reset();
    onClose();
  };

  const handleFormSubmit = async values => {
    try {
      const result = await onSubmit({
        label: values.label || undefined,
        expirationDate: values.expirationDate ?
          endOfDay(new Date(values.expirationDate)).toISOString() : undefined
      });

      setCreatedApiKey(result.apikey);
      setStep('created');
    } catch (error) {
      console.error('Error creating API key:', error);
    }
  };

  const handleCopyToClipboard = async () => {
    if (createdApiKey) {
      try {
        await navigator.clipboard.writeText(createdApiKey);
      } catch (err) {
        console.error('Failed to copy to clipboard:', err);
      }
    }
  };

  if (step === 'created') {
    return (
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          handleClose();
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>API Key Created Successfully</DialogTitle>
        <DialogContent>
          <StyledAlert severity="warning">
            <Typography variant="body2">
              <strong>Important:</strong>
              {' '}
              This is the only time you will be able to see your API key.
              Please copy it and store it in a safe place.
            </Typography>
          </StyledAlert>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Your new API key:
          </Typography>

          <KeyDisplayContainer>
            <KeyText variant="body1">
              {createdApiKey}
            </KeyText>
            <Tooltip title="Copy to clipboard">
              <IconButton onClick={handleCopyToClipboard} edge="end" size="small">
                <CopyIcon />
              </IconButton>
            </Tooltip>
          </KeyDisplayContainer>

          <Typography variant="caption" color="text.secondary">
            Make sure to copy your API key now. You won&#39;t be able to see it again!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained" color="secondary">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generate New API Key</DialogTitle>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleFormSubmit)} autoComplete="off">
          <DialogContent>
            <FormContainer>
              <RHFTextField
                name="label"
                label="Label (Optional)"
                fullWidth
                placeholder="e.g., Mobile App, Third-party Integration"
                helperText="Give your API key a descriptive name"
                InputProps={{ inputProps: { maxLength: MAX_LABEL_LENGTH } }}
              />

              <RHFDateTimeField
                name="expirationDate"
                label="Expiration Date (Optional)"
                variant="outlined"
                fullWidth
                minDate={startOfDay(addDays(new Date(), 1))}
                maxDate={MAX_EXPIRATION_DATE}
                dateonly={true}
                info="Leave empty for no expiration"
              />
            </FormContainer>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              disabled={isSubmitting || !isValid}
            >
              {isSubmitting ? 'Generating...' : 'Generate API Key'}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default CreateApiKeyDialog;
